import {
  type TonConnectProofWire,
  type TonConnectVerifyInput,
  fetchTonConnectNonce,
} from '../api/userland'

export type TonConnectProofBundle = Omit<TonConnectVerifyInput, 'vault' | 'wallet'>

type TonConnectWallet = {
  account: {
    address: string
    chain: string
    walletStateInit: string
  }
  connectItems?: {
    tonProof?: { name?: string; proof?: TonConnectProofWire } | { error: { message?: string } }
  }
}

type TonConnectUIHandle = {
  connectionRestored: Promise<unknown>
  connected: boolean
  disconnect: () => Promise<void>
  getWallets: () => Promise<unknown>
  modal?: { close: (reason?: string) => void }
  setConnectRequestParameters: (params: {
    state: 'ready'
    value: { tonProof: string }
  }) => void
  connectWallet: () => Promise<TonConnectWallet>
}

let ui: TonConnectUIHandle | null = null
let uiPromise: Promise<TonConnectUIHandle> | null = null
let noncePromise: ReturnType<typeof fetchTonConnectNonce> | null = null

export function prefetchTonConnectNonce(): ReturnType<typeof fetchTonConnectNonce> {
  if (!noncePromise) {
    noncePromise = fetchTonConnectNonce().catch((err) => {
      noncePromise = null
      throw err
    })
  }
  return noncePromise
}

export function takeTonConnectNonce(): ReturnType<typeof fetchTonConnectNonce> {
  const pending = noncePromise ?? fetchTonConnectNonce()
  noncePromise = null
  return pending
}

const TON_CONNECT_QR_MARK =
  'https://raw.githubusercontent.com/ton-connect/sdk/main/assets/ton-icon-48.png'

/** Wallets fetch this URL from the public internet, not from the operator browser. */
export function tonConnectManifestUrlFor(hostname: string, origin: string): string {
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
    return `${origin}/tonconnect-manifest.json`
  }
  return 'https://trade.wils.fun/tonconnect-manifest.json'
}

export function tonConnectManifestUrl(): string {
  return tonConnectManifestUrlFor(window.location.hostname, window.location.origin)
}

async function createUI(): Promise<TonConnectUIHandle> {
  const { TonConnectUI, THEME } = await import('@tonconnect/ui')
  const handle = new TonConnectUI({
    manifestUrl: tonConnectManifestUrl(),
    restoreConnection: false,
    uiPreferences: { theme: THEME.DARK, borderRadius: 's' },
  }) as unknown as TonConnectUIHandle
  // connectWallet() awaits getWallets() before the picker paints. Warm the
  // official list (and the QR TON mark) at boot so the click is not a second
  // of empty grey squares.
  try {
    await handle.getWallets()
  } catch {
    // SDK falls back to its bundled list; the picker still opens.
  }
  const mark = new Image()
  mark.src = TON_CONNECT_QR_MARK
  return handle
}

export function prefetchTonConnect(): Promise<TonConnectUIHandle> {
  if (!uiPromise) {
    uiPromise = createUI().then((handle) => {
      ui = handle
      return handle
    })
  }
  return uiPromise
}

async function getUI(): Promise<TonConnectUIHandle> {
  if (ui) return ui
  return prefetchTonConnect()
}

async function dropSession(handle: TonConnectUIHandle): Promise<void> {
  try {
    if (handle.connected) await handle.disconnect()
  } catch {
    // identity-only: a failed disconnect must not keep the wallet as a session
  }
}

export function isTonConnectCancelled(err: unknown): boolean {
  const msg = err instanceof Error ? err.message : String(err)
  return /Wallet was not connected|User rejects|user reject|cancelled|canceled|Popup closed|modal closed|closed by the user/i.test(
    msg
  )
}

/**
 * Ask a real wallet to sign TON Proof for `payload`.
 *
 * The ConnectEvent address is NEVER identity. fakeconnect-style wallets can
 * put any address in the event; only a valid ton_proof is accepted, and the
 * session is disconnected immediately after so the connected wallet cannot
 * become the trading recipient.
 */
export async function requestTonProof(payload: string): Promise<TonConnectProofBundle> {
  const handle = await getUI()
  // A leftover Connect session from a cancelled picker makes the next
  // connectWallet() throw “Wallet was not connected” without opening UI.
  await dropSession(handle)
  handle.setConnectRequestParameters({
    state: 'ready',
    value: { tonProof: payload },
  })
  try {
    const wallet = await handle.connectWallet()
    const proofItem = wallet.connectItems?.tonProof
    const proof = proofItem && 'proof' in proofItem ? proofItem.proof : undefined
    if (!proof || !proof.payload || !proof.signature || !proof.domain?.value) {
      throw new Error('Wallet did not sign a TON Proof. Connection refused.')
    }
    if (wallet.account.chain !== '-239') {
      throw new Error('Mainnet wallet required')
    }
    if (!wallet.account.walletStateInit) {
      throw new Error('Wallet did not provide state_init. Connection refused.')
    }
    return {
      address: wallet.account.address,
      network: wallet.account.chain,
      proof,
      state_init: wallet.account.walletStateInit,
    }
  } finally {
    await dropSession(handle)
  }
}
